import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  Tag,
  Empty,
  Timeline,
  Tabs,
  Typography,
  Spin,
  Descriptions,
  Tooltip,
} from 'antd';
import {
  Activity,
  Cpu,
  Sparkles,
  ShieldAlert,
  PenLine,
  History,
  GitBranch,
  Users,
  Database,
  Layers,
  Workflow,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import Icons from '@/components/third-party/Icons';
import StatCard from '@/components/StatCard';
import TableContainer from '@/components/TableContainer';
import { useTheme } from '@/App';
import { defaultColors, getSidebarColor } from '@/themes/antd-theme';
import { hexToRgba } from '@/hooks/useAccentTheme';
import { get_batch_list_360, get_batch_full_audit_trail } from '@/utils/api';
import { resolveOperationExecutionStatus } from '@/utils/operationExecutionStatus';
import { formatDateTime } from '@/utils/datetime';

const { Title, Text } = Typography;

const fmt = (v) => formatDateTime(v);

// Truncate info-tile values so long names don't blow out the tile height; full value shown via Tooltip.
const truncate = (v, max = 24) => {
  const s = v == null || v === '' ? '—' : String(v);
  return s.length > max ? `${s.slice(0, max)}...` : s;
};

const opStatusColor = (s = '') => {
  const v = String(s).toUpperCase();
  if (v.includes('HOLD') || v.includes('PAUSE')) return 'gold';
  if (v.includes('RUN') || v.includes('PROGRESS') || v.includes('START')) return 'blue';
  if (v.includes('COMPLET') || v.includes('DONE')) return 'green';
  if (v.includes('FAIL') || v.includes('REJECT')) return 'red';
  if (v.includes('AVAIL') || v.includes('FREE')) return 'green';
  if (v.includes('BUSY') || v.includes('OCCUP')) return 'orange';
  return 'default';
};

// Color + icon per unified-timeline source
const SOURCE_META = {
  esign: { color: '#7c3aed', label: 'E-Sign' },
  change_history: { color: '#d97706', label: 'Change' },
  machine: { color: '#0891b2', label: 'Machine' },
  recipe: { color: '#2563eb', label: 'MBMR' },
  operation: { color: '#16a34a', label: 'Operation' },
  handover: { color: '#db2777', label: 'Handover' },
  cleaning: { color: '#0d9488', label: 'Cleaning' },
  deviation: { color: '#dc2626', label: 'Deviation' },
  release: { color: '#4f46e5', label: 'Release' },
  state: { color: '#64748b', label: 'State' },
  material: { color: '#ca8a04', label: 'Material' },
  event: { color: '#475569', label: 'Event' },
};

const sourceMeta = (src = '') => {
  const key = String(src).toLowerCase();
  return SOURCE_META[key] || { color: '#94a3b8', label: src || 'Event' };
};

const severityColor = (s = '') => {
  const v = String(s).toUpperCase();
  if (v.includes('HIGH') || v.includes('CRIT')) return 'red';
  if (v.includes('MED')) return 'orange';
  if (v.includes('LOW')) return 'blue';
  return 'default';
};

const yesNo = (v) =>
  v === true ? (
    <Tag color="green">Yes</Tag>
  ) : v === false ? (
    <Tag color="red">No</Tag>
  ) : (
    <Tag>—</Tag>
  );

const Batch360 = () => {
  const { isDark } = useTheme();
  const { theme_color } = useSelector((state) => state.company_profile);
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const axisColor = isDark ? '#94a3b8' : '#64748b';
  const heroBg = getSidebarColor(theme_color, isDark);
  // Industrial Green (theme's success token) — lighter [500] shade so it stays visible on the dark hero band.
  const successPillBg = hexToRgba(defaultColors.palette.green[500], 0.3);
  const neutralPillBg = 'rgba(255,255,255,0.15)';

  const [batches, setBatches] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load batch list for the selector (unchanged shape: res.data.data)
  useEffect(() => {
    get_batch_list_360(search, (res) => {
      if (res?.data?.status === 'success') setBatches(res.data.data || []);
    });
  }, [search]);

  // Load full audit trail when a batch is picked.
  // NOTE: audit data is at the TOP LEVEL of res.data (NOT res.data.data).
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    setLoading(true);
    get_batch_full_audit_trail(selected, (res) => {
      const d = res?.data;
      if (d?.status === 'success') setDetail(d);
      else setDetail(null);
      setLoading(false);
    });
  }, [selected]);

  const summary = detail?.summary || {};
  const batch = detail?.batch || {};
  const recipeConfig = detail?.recipe_config || {};
  const operations = detail?.operations || [];
  // Group operations by their parent process for the "By Process" view.
  const processGroups = (() => {
    const map = new Map();
    (operations || []).forEach((op) => {
      const name = op?.process || 'Unassigned';
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(op);
    });
    return Array.from(map.entries()).map(([process, ops]) => ({ process, ops }));
  })();
  const machineOccupancy = detail?.machine_occupancy || {};
  const machines = machineOccupancy.machines || [];
  const cleaning = machineOccupancy.cleaning || [];
  const stateHistory = detail?.state_history || [];
  const deviations = detail?.deviations || [];
  const esign = detail?.esign || [];
  const changeHistory = detail?.change_history || [];
  const events = detail?.events || [];
  const materialLot = detail?.material_lot_audit || [];
  const handovers = detail?.employee_handover || [];
  const timeline = detail?.timeline || [];

  const finalize = recipeConfig.finalize || [];
  const release = recipeConfig.release || [];
  const recipeMeta = recipeConfig.recipe || {};

  const batchOptions = useMemo(
    () =>
      (batches || []).map((b) => ({
        value: b.batch_number,
        label: `${b.batch_number} · ${b.product || b.recipe || ''} (${b.status})`,
      })),
    [batches]
  );

  // ---- Column defs ----
  const opCols = [
    { title: '#', dataIndex: 'operation_number', key: 'operation_number', width: 60 },
    { title: 'Operation', dataIndex: 'operation_name', key: 'operation_name' },
    { title: 'Type', dataIndex: 'operation_type', key: 'operation_type', render: (v) => v || '—' },
    { title: 'Process', dataIndex: 'process', key: 'process', render: (v) => v || '—' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (_, record) => {
        const displayStatus = record.status || record.operation_status;
        if (!displayStatus) return '—';
        const resolved = resolveOperationExecutionStatus({
          ...record,
          status: displayStatus,
          operation_status: displayStatus,
        });
        return (
          <span>
            <Tag color={resolved.style.tagColor}>{resolved.label}</Tag>
            {resolved.sublabel ? (
              <span
                style={{
                  display: 'block',
                  fontSize: 10,
                  color: axisColor,
                  marginTop: 2,
                  lineHeight: 1.3,
                }}
              >
                {resolved.sublabel}
              </span>
            ) : null}
          </span>
        );
      },
    },
    { title: 'Machine', dataIndex: 'machine', key: 'machine', render: (m) => m?.name || '—' },
    { title: 'Start', dataIndex: 'batch_start_time', key: 'batch_start_time', render: fmt },
    { title: 'End', dataIndex: 'batch_end_time', key: 'batch_end_time', render: fmt },
    { title: 'Started By', dataIndex: 'started_by', key: 'started_by', render: (v) => v || '—' },
    { title: 'Ended By', dataIndex: 'ended_by', key: 'ended_by', render: (v) => v || '—' },
    { title: 'Reviewed By', dataIndex: 'reviewed_by', key: 'reviewed_by', render: (v) => v || '—' },
    { title: 'Reviewed At', dataIndex: 'reviewed_at', key: 'reviewed_at', render: fmt },
    {
      title: 'Paused (s)',
      dataIndex: 'total_paused_seconds',
      key: 'total_paused_seconds',
      render: (v) => v ?? '—',
    },
  ];

  const machineCols = [
    { title: 'Machine', dataIndex: 'machine_name', key: 'machine_name', render: (v) => v || '—' },
    { title: 'Code', dataIndex: 'machine_code', key: 'machine_code', render: (v) => v || '—' },
    {
      title: 'Current Status',
      dataIndex: 'current_status',
      key: 'current_status',
      render: (v) => <Tag color={opStatusColor(v)}>{v || '—'}</Tag>,
    },
    {
      title: 'Available',
      dataIndex: 'is_currently_available',
      key: 'is_currently_available',
      render: yesNo,
    },
    {
      title: 'Total Events',
      dataIndex: 'total_events',
      key: 'total_events',
      render: (v) => v ?? '—',
    },
    {
      title: 'Busy Time',
      dataIndex: 'total_busy_time',
      key: 'total_busy_time',
      render: (v) => v ?? '—',
    },
  ];

  const cleaningCols = [
    { title: 'Machine', dataIndex: 'machine_name', key: 'machine_name', render: (v) => v || '—' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => <Tag color={opStatusColor(v)}>{v || '—'}</Tag>,
    },
    { title: 'Type', dataIndex: 'cleaning_type', key: 'cleaning_type', render: (v) => v || '—' },
    { title: 'Started By', dataIndex: 'started_by', key: 'started_by', render: (v) => v || '—' },
    { title: 'Started', dataIndex: 'started_at', key: 'started_at', render: fmt },
    {
      title: 'Completed By',
      dataIndex: 'completed_by',
      key: 'completed_by',
      render: (v) => v || '—',
    },
    { title: 'Completed', dataIndex: 'completed_at', key: 'completed_at', render: fmt },
    {
      title: 'Duration (min)',
      dataIndex: 'duration_minutes',
      key: 'duration_minutes',
      render: (v) => v ?? '—',
    },
    {
      title: 'Verification',
      dataIndex: 'verification_method',
      key: 'verification_method',
      render: (v) => v || '—',
    },
    {
      title: 'Approvals',
      dataIndex: 'approvals',
      key: 'approvals',
      render: (aps) =>
        !aps || aps.length === 0 ? (
          '—'
        ) : (
          <div className="flex flex-col gap-1">
            {aps.map((a, i) => (
              <Tag
                key={i}
                color={
                  a.status === 'APPROVED' ? 'green' : a.status === 'REJECTED' ? 'red' : 'orange'
                }
              >
                {a.role ? `${a.role}: ` : ''}
                {a.status}
              </Tag>
            ))}
          </div>
        ),
    },
  ];

  const changeCols = [
    { title: 'Time', dataIndex: 'timestamp', key: 'timestamp', render: fmt, width: 160 },
    { title: 'Table', dataIndex: 'table_name', key: 'table_name', render: (v) => v || '—' },
    { title: 'Field', dataIndex: 'field_name', key: 'field_name', render: (v) => v || '—' },
    {
      title: 'Change',
      dataIndex: 'change_type',
      key: 'change_type',
      render: (v) => <Tag color={opStatusColor(v)}>{v || '—'}</Tag>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (v) => (v ? <Tag color={severityColor(v)}>{v}</Tag> : '—'),
    },
    {
      title: 'Old → New',
      key: 'old_new',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          <Text delete type="secondary">
            {r.old_value ?? '—'}
          </Text>
          {' → '}
          <Text strong>{r.new_value ?? '—'}</Text>
        </span>
      ),
    },
    { title: 'User', dataIndex: 'user', key: 'user', render: (v) => v || '—' },
    { title: 'IP', dataIndex: 'ip_address', key: 'ip_address', render: (v) => v || '—' },
    { title: 'Reason', dataIndex: 'change_reason', key: 'change_reason', render: (v) => v || '—' },
  ];

  const esignCols = [
    { title: 'Action', dataIndex: 'action_type', key: 'action_type', render: (v) => v || '—' },
    { title: 'Category', dataIndex: 'category', key: 'category', render: (v) => v || '—' },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' },
    { title: 'Done By', dataIndex: 'done_by', key: 'done_by', render: (v) => v || '—' },
    {
      title: 'User Verified',
      dataIndex: 'is_user_verified',
      key: 'is_user_verified',
      render: yesNo,
    },
    {
      title: 'Cert Verified',
      dataIndex: 'is_verified_by_certificate',
      key: 'is_verified_by_certificate',
      render: yesNo,
    },
    { title: 'Comment', dataIndex: 'comment', key: 'comment', render: (v) => v || '—' },
    { title: 'Date', dataIndex: 'addition_dt', key: 'addition_dt', render: fmt },
  ];

  const handoverCols = [
    { title: 'Employee', dataIndex: 'employee', key: 'employee', render: (v) => v || '—' },
    { title: 'Process', dataIndex: 'process', key: 'process', render: (v) => v || '—' },
    {
      title: 'Available',
      dataIndex: 'is_employee_available',
      key: 'is_employee_available',
      render: yesNo,
    },
    { title: 'Read', dataIndex: 'is_read', key: 'is_read', render: yesNo },
    { title: 'Comment', dataIndex: 'comment', key: 'comment', render: (v) => v || '—' },
    { title: 'Date', dataIndex: 'handover_dt', key: 'handover_dt', render: fmt },
  ];

  // Generic fallback columns for the often-empty arrays (deviations / state / events / material)
  const genericCols = (sample) => {
    const keys = sample && typeof sample === 'object' ? Object.keys(sample) : [];
    return keys.map((k) => ({
      title: k.replace(/_/g, ' '),
      dataIndex: k,
      key: k,
      render: (v) => {
        if (v == null || v === '') return '—';
        if (typeof v === 'boolean') return yesNo(v);
        if (typeof v === 'object') return JSON.stringify(v);
        if (/_dt$|_date$|_time$|timestamp|_at$/.test(k)) return fmt(v);
        return String(v);
      },
    }));
  };

  const statusTags = (
    <>
      {batch.is_terminated && <Tag color="red">Terminated</Tag>}
      {batch.stop_status && <Tag color="volcano">{batch.stop_status}</Tag>}
      {batch.is_packaging && <Tag color="purple">Packaging</Tag>}
      {batch.batch_type && <Tag color="blue">{batch.batch_type}</Tag>}
      {batch.is_terminated == null && !batch.stop_status && !batch.deleted_dt && (
        <Tag color="green">Active</Tag>
      )}
    </>
  );

  // Timeline height — vh-anchored so it scales across screen sizes instead of a flat px.
  const TIMELINE_MAX_HEIGHT = 'calc(100vh - 468px)';
  // Right detail panel (tabs + table) — kept independent of TIMELINE_MAX_HEIGHT so reducing the
  // timeline's height doesn't also shrink the tables.
  const PANEL_BODY_HEIGHT = 'calc(100vh - 432px)';
  // Leave extra room below the tab bar (beyond its ~56px) so the table's horizontal scrollbar
  // has clear space and stays visible inside PANEL_BODY_HEIGHT instead of hugging its edge.
  const TABLE_SCROLL_Y = 'calc(100vh - 560px)';

  // Icon + label on a single line (no wrapping) for tabs and card titles.
  const iconLabel = (icon, text) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      {icon}
      {text}
    </span>
  );

  const renderTab = (rows, cols, emptyText) => (
    <TableContainer
      className="batch360-table"
      size="small"
      data={(rows || []).map((r, i) => ({ ...r, key: i }))}
      columns={cols}
      height={TABLE_SCROLL_Y}
      scrollX="max-content"
      locale={{ emptyText: <Empty description={emptyText} /> }}
    />
  );

  return (
    <div className="p-2.5 pt-5">
      {/* Thinner but clearly visible horizontal scrollbar on every table in this page, so users
          notice there's more content to scroll to (default browser scrollbar blends in / is easy to miss). */}
      <style>{`
        .batch360-table .ant-table-body,
        .batch360-table .ant-table-content,
        .batch360-scroll-panel {
          scrollbar-width: thin;
          scrollbar-color: ${isDark ? '#64748b' : '#94a3b8'} transparent;
        }
        .batch360-table .ant-table-body::-webkit-scrollbar,
        .batch360-table .ant-table-content::-webkit-scrollbar,
        .batch360-scroll-panel::-webkit-scrollbar {
          height: 6px;
          width: 6px;
        }
        .batch360-table .ant-table-body::-webkit-scrollbar-track,
        .batch360-table .ant-table-content::-webkit-scrollbar-track,
        .batch360-scroll-panel::-webkit-scrollbar-track {
          background: transparent;
        }
        .batch360-table .ant-table-body::-webkit-scrollbar-thumb,
        .batch360-table .ant-table-content::-webkit-scrollbar-thumb,
        .batch360-scroll-panel::-webkit-scrollbar-thumb {
          background: ${isDark ? '#64748b' : '#94a3b8'};
          border-radius: 4px;
        }
        .batch360-table .ant-table-body::-webkit-scrollbar-thumb:hover,
        .batch360-table .ant-table-content::-webkit-scrollbar-thumb:hover,
        .batch360-scroll-panel::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? '#94a3b8' : '#64748b'};
        }
      `}</style>
      <Card
        className="border rounded-lg border-gray-300 shadow-md"
        bordered={false}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          minHeight: 'calc(100vh - 120px)',
        }}
        styles={{
          body: { padding: 10 },
          header: {
            padding: '8px 12px',
            minHeight: 0,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          },
        }}
        title={
          <div className="flex items-center gap-2">
            <Icons.ClipboardList className="text-blue-600" size={24} />
            <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
              Batch Traceability
            </Title>
          </div>
        }
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Select
              showSearch
              allowClear
              placeholder="Select a batch…"
              style={{ minWidth: 320 }}
              value={selected}
              onChange={setSelected}
              onSearch={setSearch}
              filterOption={false}
              options={batchOptions}
              notFoundContent={<Empty description="No batches" />}
            />
          </div>
        }
      >
        {!selected ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 14, color: '#475569' }}>
                Please select a batch from the dropdown above to view its full traceability
              </span>
            }
            style={{ padding: '48px 0' }}
          />
        ) : loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" tip="Loading audit trail…" />
          </div>
        ) : !detail || summary.batch_found === false ? (
          <Empty description="No audit trail available for this batch" />
        ) : (
          <>
            {/* HEADER */}
            <Card
              size="small"
              style={{ marginBottom: 16, overflow: 'hidden' }}
              styles={{ body: { padding: 0 } }}
            >
              {/* Hero band — follows the company's configured theme color (same as the Navigation sidebar) */}
              <div
                style={{
                  background: heroBg,
                  padding: '10px 20px',
                  color: '#fff',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <Icons.ClipboardList size={18} />
                    <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.2 }}>
                      {batch.batch_number || detail.batch_number || '—'}
                    </span>
                    <span>{statusTags}</span>
                  </div>
                  <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>
                    {batch.recipe?.name || recipeMeta.name || '—'}
                    {recipeMeta.recipe_code ? ` · ${recipeMeta.recipe_code}` : ''}
                    {batch.product?.name ? ` · ${batch.product.name}` : ''}
                  </div>
                </div>
                {/* Finalize / Release status pills */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <div
                    style={{
                      background: finalize.length > 0 ? successPillBg : neutralPillBg,
                      borderRadius: 8,
                      padding: '5px 10px',
                      fontSize: 11,
                    }}
                  >
                    <div
                      style={{
                        opacity: 0.75,
                        fontSize: 9,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      Finalized
                    </div>
                    {finalize.length > 0 ? (
                      <div style={{ fontWeight: 600 }}>
                        {finalize[0].approved_by || '—'} · {fmt(finalize[0].approved_at)}
                      </div>
                    ) : (
                      <div style={{ fontWeight: 600, opacity: 0.8 }}>Not finalized</div>
                    )}
                  </div>
                  <div
                    style={{
                      background: release.length > 0 ? successPillBg : neutralPillBg,
                      borderRadius: 8,
                      padding: '5px 10px',
                      fontSize: 11,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        opacity: 0.75,
                        fontSize: 9,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      Release
                    </div>
                    <div style={{ fontWeight: 700 }}>
                      {release.length > 0 ? `Released (${release.length})` : 'Not Released'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Info tiles */}
              <Row gutter={0}>
                {[
                  {
                    label: 'Recipe',
                    value: `${batch.recipe?.name || recipeMeta.name || '—'}${
                      recipeMeta.recipe_code ? ` (${recipeMeta.recipe_code})` : ''
                    }`,
                    icon: <Icons.FlaskConical size={16} />,
                    color: '#2563eb',
                  },
                  {
                    label: 'Product',
                    value: batch.product?.name || '—',
                    icon: <Icons.Package size={16} />,
                    color: '#7c3aed',
                  },
                  {
                    label: 'Machine',
                    value: batch.machine?.name || '—',
                    icon: <Cpu size={16} />,
                    color: '#0891b2',
                  },
                  {
                    label: 'Batch Type',
                    value: batch.batch_type || '—',
                    icon: <Icons.Layers size={16} />,
                    color: '#0d9488',
                  },
                  {
                    label: 'Batch Size',
                    value: batch.batch_size ?? '—',
                    icon: <Icons.Scale size={16} />,
                    color: '#d97706',
                  },
                  {
                    label: 'Start',
                    value: fmt(batch.batch_start_time || batch.start_datetime),
                    icon: <Icons.PlayCircle size={16} />,
                    color: '#16a34a',
                  },
                  {
                    label: 'End',
                    value: fmt(batch.end_datetime),
                    icon: <Icons.StopCircle size={16} />,
                    color: batch.end_datetime ? '#dc2626' : '#94a3b8',
                  },
                  {
                    label: 'Paused',
                    value: `${batch.total_paused_seconds ?? 0}s`,
                    icon: <Icons.Clock size={16} />,
                    color: '#64748b',
                  },
                ].map((f, i, arr) => (
                  <Col xs={12} sm={8} md={6} lg={3} key={f.label}>
                    <div
                      style={{
                        padding: '12px 14px',
                        borderRight: (i + 1) % 8 === 0 ? 'none' : '1px solid #f0f0f0',
                        borderBottom: '1px solid #f0f0f0',
                        height: '100%',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          color: f.color,
                          marginBottom: 4,
                        }}
                      >
                        {f.icon}
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            color: axisColor,
                          }}
                        >
                          {f.label}
                        </span>
                      </div>
                      <Tooltip
                        title={f.value == null || f.value === '' ? '' : String(f.value)}
                        color={defaultColors.primary}
                        styles={{
                          body: {
                            fontSize: 12.5,
                            fontWeight: 500,
                            padding: '6px 10px',
                            borderRadius: 6,
                          },
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: textColor,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {truncate(f.value)}
                        </div>
                      </Tooltip>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* SUMMARY STAT CARDS */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={8} md={6} lg={3}>
                <StatCard
                  label="Operations"
                  value={summary.operations_count ?? 0}
                  icon={<Workflow size={24} />}
                  bgColor="#dcfce7"
                  iconColor="#16a34a"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={3}>
                <StatCard
                  label="Machine Events"
                  value={summary.machine_events ?? 0}
                  icon={<Cpu size={24} />}
                  bgColor="#cffafe"
                  iconColor="#0891b2"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={3}>
                <StatCard
                  label="Cleaning"
                  value={summary.cleaning_records ?? 0}
                  icon={<Sparkles size={24} />}
                  bgColor="#ccfbf1"
                  iconColor="#0d9488"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={3}>
                <StatCard
                  label="Change History"
                  value={summary.change_history_entries ?? 0}
                  icon={<History size={24} />}
                  bgColor="#fef3c7"
                  iconColor="#d97706"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={3}>
                <StatCard
                  label="E-Sign"
                  value={summary.esign_events ?? 0}
                  icon={<PenLine size={24} />}
                  bgColor="#ede9fe"
                  iconColor="#7c3aed"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={3}>
                <StatCard
                  label="Deviations"
                  value={summary.deviations ?? 0}
                  icon={<ShieldAlert size={24} />}
                  bgColor="#fee2e2"
                  iconColor="#dc2626"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={3}>
                <StatCard
                  label="Handovers"
                  value={summary.handovers ?? 0}
                  icon={<Users size={24} />}
                  bgColor="#fce7f3"
                  iconColor="#db2777"
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={3}>
                <StatCard
                  label="Timeline"
                  value={summary.timeline_entries ?? timeline.length}
                  icon={<Activity size={24} />}
                  bgColor="#dbeafe"
                  iconColor="#2563eb"
                />
              </Col>
            </Row>

            <Row gutter={[12, 12]}>
              {/* UNIFIED TIMELINE — centerpiece */}
              <Col xs={24} lg={9}>
                <Card
                  size="small"
                  title={iconLabel(<GitBranch size={15} />, 'Unified Audit Timeline')}
                  styles={{
                    body: { maxHeight: TIMELINE_MAX_HEIGHT, overflowY: 'auto', paddingTop: 14 },
                  }}
                >
                  {timeline.length === 0 ? (
                    <Empty description="No timeline entries" />
                  ) : (
                    <Timeline
                      items={timeline.map((t, i) => {
                        const meta = sourceMeta(t.source);
                        return {
                          key: i,
                          color: meta.color,
                          children: (
                            <div style={{ fontSize: 13 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Tag color={meta.color} style={{ marginRight: 0 }}>
                                  {meta.label}
                                </Tag>
                                <b>{t.action || '—'}</b>
                              </div>
                              {t.summary && (
                                <div style={{ color: textColor, marginTop: 2 }}>{t.summary}</div>
                              )}
                              <div style={{ color: axisColor, fontSize: 12, marginTop: 2 }}>
                                {fmt(t.timestamp)}
                                {t.actor ? ` · ${t.actor}` : ''}
                                {t.ref_id ? ` · #${t.ref_id}` : ''}
                              </div>
                            </div>
                          ),
                        };
                      })}
                    />
                  )}
                </Card>
              </Col>

              {/* DETAIL TABS */}
              <Col xs={24} lg={15}>
                <Card
                  size="small"
                  styles={{
                    body: { height: PANEL_BODY_HEIGHT, paddingTop: 8, overflow: 'hidden' },
                  }}
                >
                  <Tabs
                    items={[
                      {
                        key: 'operations',
                        label: iconLabel(
                          <Workflow size={14} />,
                          `Operations (${operations.length})`
                        ),
                        children: renderTab(operations, opCols, 'No operations'),
                      },
                      {
                        key: 'by-process',
                        label: iconLabel(
                          <Layers size={14} />,
                          `By Process (${processGroups.length})`
                        ),
                        children:
                          processGroups.length === 0 ? (
                            <Empty description="No process data" />
                          ) : (
                            <div
                              className="batch360-scroll-panel"
                              style={{ maxHeight: TABLE_SCROLL_Y, overflowY: 'auto' }}
                            >
                              {processGroups.map((g, gi) => (
                                <div key={gi} style={{ marginBottom: 16 }}>
                                  <Text strong style={{ display: 'block', marginBottom: 6 }}>
                                    {g.process}{' '}
                                    <Tag color="blue" style={{ marginInlineStart: 4 }}>
                                      {g.ops.length} operation{g.ops.length === 1 ? '' : 's'}
                                    </Tag>
                                  </Text>
                                  <TableContainer
                                    className="batch360-table"
                                    size="small"
                                    data={g.ops.map((o, i) => ({ ...o, key: i }))}
                                    columns={opCols}
                                    height={TABLE_SCROLL_Y}
                                    scrollX="max-content"
                                  />
                                </div>
                              ))}
                            </div>
                          ),
                      },
                      {
                        key: 'machines',
                        label: iconLabel(<Cpu size={14} />, 'Machines & Cleaning'),
                        children: (
                          <div
                            className="batch360-scroll-panel"
                            style={{ maxHeight: TABLE_SCROLL_Y, overflowY: 'auto' }}
                          >
                            <Text strong style={{ display: 'block', marginBottom: 6 }}>
                              Machine Occupancy
                            </Text>
                            <TableContainer
                              className="batch360-table"
                              size="small"
                              data={machines.map((m, i) => ({ ...m, key: i }))}
                              columns={machineCols}
                              height={TABLE_SCROLL_Y}
                              scrollX="max-content"
                              expandable={{
                                expandedRowRender: (m) =>
                                  (m.occupancy_windows || []).length === 0 ? (
                                    <Empty
                                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                                      description="No occupancy windows"
                                    />
                                  ) : (
                                    <Timeline
                                      style={{ marginTop: 8 }}
                                      items={(m.occupancy_windows || []).map((w, i) => ({
                                        key: i,
                                        color: w.ongoing ? 'orange' : 'green',
                                        children: (
                                          <span style={{ fontSize: 12 }}>
                                            <b>Op #{w.operation_number ?? '—'}</b> ·{' '}
                                            {w.start_action || '—'} →{' '}
                                            {w.end_action || (w.ongoing ? 'ongoing' : '—')}
                                            <div style={{ color: axisColor }}>
                                              {fmt(w.occupied_at)} →{' '}
                                              {w.ongoing ? 'ongoing' : fmt(w.released_at)}
                                              {w.duration != null ? ` · ${w.duration}` : ''}
                                            </div>
                                          </span>
                                        ),
                                      }))}
                                    />
                                  ),
                                rowExpandable: (m) => (m.occupancy_windows || []).length > 0,
                              }}
                              locale={{ emptyText: <Empty description="No machine occupancy" /> }}
                            />
                            <Text strong style={{ display: 'block', margin: '16px 0 6px' }}>
                              Cleaning Records ({cleaning.length})
                            </Text>
                            <TableContainer
                              className="batch360-table"
                              size="small"
                              data={cleaning.map((c, i) => ({ ...c, key: i }))}
                              columns={cleaningCols}
                              height={TABLE_SCROLL_Y}
                              scrollX="max-content"
                              locale={{ emptyText: <Empty description="No cleaning records" /> }}
                            />
                          </div>
                        ),
                      },
                      {
                        key: 'change',
                        label: iconLabel(
                          <History size={14} />,
                          `Change History (${changeHistory.length})`
                        ),
                        children: renderTab(changeHistory, changeCols, 'No change history'),
                      },
                      {
                        key: 'esign',
                        label: iconLabel(<PenLine size={14} />, `E-Sign (${esign.length})`),
                        children: renderTab(esign, esignCols, 'No e-sign events'),
                      },
                      {
                        key: 'handovers',
                        label: iconLabel(<Users size={14} />, `Handovers (${handovers.length})`),
                        children: renderTab(handovers, handoverCols, 'No handovers'),
                      },
                      {
                        key: 'deviations',
                        label: iconLabel(
                          <ShieldAlert size={14} />,
                          `Deviations (${deviations.length})`
                        ),
                        children: renderTab(
                          deviations,
                          genericCols(deviations[0]),
                          'No deviations'
                        ),
                      },
                      {
                        key: 'state',
                        label: iconLabel(
                          <Layers size={14} />,
                          `State History (${stateHistory.length})`
                        ),
                        children: renderTab(
                          stateHistory,
                          genericCols(stateHistory[0]),
                          'No state history'
                        ),
                      },
                      {
                        key: 'events',
                        label: iconLabel(<Activity size={14} />, `Events (${events.length})`),
                        children: renderTab(events, genericCols(events[0]), 'No events'),
                      },
                      {
                        key: 'material',
                        label: iconLabel(
                          <Database size={14} />,
                          `Material Lots (${materialLot.length})`
                        ),
                        children: renderTab(
                          materialLot,
                          genericCols(materialLot[0]),
                          'No material lot movements'
                        ),
                      },
                    ]}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Card>
    </div>
  );
};

export default Batch360;
